import { useNavigate } from 'react-router-dom';

const services = [
  {
    title: 'AI Stress Testing',
    description: 'We put AI systems through extreme conditions to find weaknesses before they cause problems. Our testing frameworks have validated billion-dollar AI deployments.',
    icon: '🔬',
    features: ['Model performance testing', 'Load & stress testing', 'Security & compliance', 'Adversarial testing'],
    route: '/services/ai-stress-testing',
    available: 'Detailed technical documentation'
  },
  {
    title: 'Procurement Intelligence',
    description: 'AI-powered procurement optimization that reduces costs and improves supply chain. Average 18% savings through intelligent spend analysis.',
    icon: '📊',
    features: ['Cost optimization AI', 'Supplier intelligence', 'Predictive analytics', 'Contract intelligence'],
    route: '/services/procurement-intelligence',
    available: 'Case studies & ROI calculators'
  },
  {
    title: 'Utility Network Intelligence',
    description: 'SimNet™ suite for water, gas, and utility infrastructure management. Detect leaks 48 hours before they surface.',
    icon: '🔧',
    features: ['SimNet™ gas simulation', 'Water network modeling', 'Leak detection AI', 'SCADA integration'],
    route: '/services/water-gas-management',
    available: 'SimNet™ software specifications'
  },
  {
    title: 'Project Management SaaS',
    description: 'Enterprise-grade SaaS platform for project and resource management with AI-powered insights.',
    icon: '📋',
    features: ['Resource planning', 'Timeline optimization', 'Budget tracking', 'Team collaboration'],
    route: '/services/project-management',
    available: 'Platform capabilities overview'
  },
  {
    title: 'Digital Transformation',
    description: 'End-to-end AI implementation for enterprises across healthcare, manufacturing, retail, energy, and government.',
    icon: '🚀',
    features: ['AI strategy consulting', 'Implementation roadmap', 'Change management', 'Training & support'],
    route: '/services/digital-transformation',
    available: 'Transformation frameworks'
  }
];

const ServicesOverviewPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      <button
        onClick={() => { navigate('/'); window.scrollTo(0, 0); }}
        data-testid="back-to-home"
        className="fixed top-6 left-6 z-50 flex items-center space-x-2 text-white hover:text-[#00E600] transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span className="font-semibold">Back to Home</span>
      </button>

      <section className="relative min-h-[50vh] flex items-center justify-center px-6 pt-24 pb-16">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHx0ZWNobm9sb2d5fGVufDB8fHx8MTc2MTA0Njc0OHww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 uppercase tracking-wide">
            Our <span className="text-[#00E600]">Services</span>
          </h1>
          <p className="text-xl text-[#B3B3B3] leading-relaxed">
            Production-grade AI and machine learning solutions that transform how enterprises operate.
            From stress testing to infrastructure intelligence.
          </p>
        </div>
      </section>

      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                onClick={() => { navigate(service.route); window.scrollTo(0, 0); }}
                className="group bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300 cursor-pointer hover:shadow-[0_0_30px_rgba(0,230,0,0.2)]"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-6 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                  <span className="text-2xl">{service.icon}</span>
                </div>

                <h3 className="text-xl font-bold text-white mb-3 uppercase tracking-wide">
                  {service.title}
                </h3>

                <p className="text-[#B3B3B3] text-sm mb-4 leading-relaxed">
                  {service.description}
                </p>

                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-2 text-sm text-[#B3B3B3]">
                      <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <p className="text-xs text-[#00E600] mb-4">{service.available}</p>

                <div className="text-[#00E600] text-sm font-semibold uppercase tracking-wide flex items-center space-x-1 group-hover:translate-x-1 transition-transform duration-300">
                  <span>View Details</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6 uppercase tracking-wide">
            Ready to Get <span className="text-[#00E600]">Started</span>?
          </h2>
          <p className="text-[#B3B3B3] mb-8">
            Contact us for a free consultation to discuss your project requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => { navigate('/'); setTimeout(() => { document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }, 100); }}
              className="px-8 py-3 bg-gradient-to-r from-[#00E600] to-[#36E0A8] text-[#0D0D0D] font-bold rounded-md hover:shadow-[0_0_20px_rgba(0,230,0,0.6)] transition-all duration-300 uppercase tracking-wide"
            >
              Contact Us
            </button>
            <button
              onClick={() => { navigate('/live-demos'); window.scrollTo(0, 0); }}
              className="px-8 py-3 border border-[#00E600] text-[#00E600] font-bold rounded-md hover:bg-[#00E600] hover:text-[#0D0D0D] transition-all duration-300 uppercase tracking-wide"
            >
              View Live Demos
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesOverviewPage;
